import { PlaceholderKind } from '@woundtech/ui-constants';
import {
  ADDRESS_TEMPORAL_STATUS_OPTIONS,
  LOCATION_ID,
  LOCATION_NAME,
  SERVICE_ADDRESS_URL,
} from 'fhir/general/constants';
import { shortDate } from 'styleguide/form/Date/formatters';
import { INPUT_MASK_POSTAL_CODE, textMaskFormat } from 'styleguide/form/Text';
import { getAddressMarketName, getAddressTemporalStatus } from './helperts';

export const cityAndStateFormat = (data: fhir.Address | undefined) => {
  return data
    ? [data?.city, data?.state].filter((value) => !!value).join(', ')
    : PlaceholderKind.NotAvailable;
};

export const stateAndPostalCodeFormat = (data: fhir.Address | undefined) => {
  return data
    ? [data?.state, textMaskFormat(data?.postalCode, INPUT_MASK_POSTAL_CODE)]
        .filter((value) => !!value)
        .join(' ')
    : PlaceholderKind.NotAvailable;
};

export const address = (data: fhir.Address | undefined, seprateor = '') => {
  const lines = data?.line?.filter((x) => x !== '') || [];
  const addressToShow =
    seprateor === '--'
      ? [
          lines?.join(', '),
          '--',
          data?.city,
          data?.state,
          textMaskFormat(data?.postalCode, INPUT_MASK_POSTAL_CODE),
        ]
      : [
          lines?.join(', '),
          data?.city,
          data?.state,
          textMaskFormat(data?.postalCode, INPUT_MASK_POSTAL_CODE),
        ];

  return data
    ? addressToShow.filter((value) => !!value).join(', ')
    : PlaceholderKind.NotAvailable;
};

export const locationNameAndAddress = (data): string => {
  const addressData = address(data);
  const locationName = (data?.extension || [])?.find(
    (item) => item.url === LOCATION_NAME
  )?.valueString;
  return locationName ? `${locationName}, ${addressData}` : addressData;
};
export const patientPcpLocationNameAndAddress = (
  data: fhir.Address[] | undefined,
  extension: fhir.Extension[]
): string => {
  const locationId = extension?.find(
    (item) => item?.url === LOCATION_ID
  )?.valueString;
  const filteredAddress = data?.filter((a) =>
    a?.extension?.find((item) => item.valueString === locationId)
  )?.[0];
  const locationName =
    (filteredAddress?.extension ?? [])?.find(
      (item) => item.url === LOCATION_NAME
    )?.valueString ?? '';
  const addressData = address(filteredAddress ?? data?.[0]);
  return locationName ? `${locationName}, ${addressData}` : addressData;
};
export const addresses = (data: fhir.Address[] | undefined) => {
  return (
    (data || []).map((x) => address(x)).join() || PlaceholderKind.NotAvailable
  );
};

export const serviceAddress = (data: fhir.Address[] | undefined) => {
  const serviceAddresses =
    data?.filter((item) => {
      return item?.extension?.find(
        (extension) =>
          extension.url === SERVICE_ADDRESS_URL &&
          extension.valueBoolean === true
      );
    }) || [];

  if (serviceAddresses?.length < 1) {
    return PlaceholderKind.NotAvailable;
  }

  return addresses(serviceAddresses);
};

export const addressCountyAndMarket = (data: fhir.Address | undefined) => {
  if (!data) {
    return PlaceholderKind.NotAvailable;
  }

  const market = getAddressMarketName(data);
  const value = [data?.district, market].filter((value) => !!value).join(', ');

  return value || PlaceholderKind.NotAvailable;
};

export const formatRoutingAddress = (address: string | undefined) => {
  return (
    address
      ?.split(',')
      ?.slice(0, -1)
      ?.filter((e) => e)
      ?.join(', ') || ''
  );
};

export const formatAddressTemporalStatus = (data: fhir.Address | undefined) => {
  const status = getAddressTemporalStatus(data);
  if (!status) {
    return undefined;
  }

  return (
    ADDRESS_TEMPORAL_STATUS_OPTIONS.find(
      (option) => option.coding?.[0]?.code === status
    )?.coding?.[0]?.display || status
  );
};

export const formatAddressPeriod = (data: fhir.Address | undefined) => {
  const start = data?.period?.start ? shortDate(data.period.start) : undefined;
  const end = data?.period?.end ? shortDate(data.period.end) : undefined;

  if (start && end) {
    return `${start} - ${end}`;
  }

  return start || end;
};
